/**
 * @fileoverview Left-nav menu, styled for the navy sidebar.
 * @description Renders NAV_ITEMS filtered down to whichever ones the signed-in user's privileges
 * allow. Takes `privileges` as a prop rather than resolving them itself — `shared/` may not import
 * a feature (ADR-0002), so DashboardShell (the app/ layer, which already resolves GET /identity/me
 * before rendering the dashboard at all) passes the resolved list down through DashboardLayout.
 * Icons stay the same static SVG asset files imported as URLs (matching this app's existing icon
 * convention), but are rendered as a CSS `mask-image` on a colored `<span>` rather than an `<img>`,
 * since an `<img>`'s referenced SVG content can't be recolored from outside the file. Painting the
 * mask with `background-color: currentColor` (the default) means the icon automatically inherits
 * whatever text color the active/inactive NavLink state has already set via Tailwind's
 * `text-*` classes — no per-item active check, duplicate icon file, or inlined SVG markup needed;
 * `width`/`height`/`color` are still exposed as props in case this icon set is ever reused at a
 * different size or a fixed (non-inherited) color. The optional onNavigate fires on every link
 * click — DashboardLayout uses it to close its mobile off-canvas drawer as part of the click
 * gesture itself, rather than deriving "close on route change" from a useLocation effect (which
 * the set-state-in-effect lint rule flags as an unconditional setState-in-effect risk). An optional
 * `collapsed` prop switches every row to icon-only (label kept for assistive tech, plus a native
 * tooltip), for DashboardLayout's desktop sidebar-collapse toggle. An item's own `dividerAfter`
 * flag (see nav-items.constants.ts) renders a thin rule right below it, for grouping.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router'

import iconAssetQuality from '@/assets/svg/nav/icon-asset-quality.svg'
import iconAuditLog from '@/assets/svg/nav/icon-audit-log.svg'
import iconBankDetails from '@/assets/svg/nav/icon-bank-details.svg'
import iconBranchAnalytics from '@/assets/svg/nav/icon-branch-analytics.svg'
import iconBusinessPerformance from '@/assets/svg/nav/icon-business-performance.svg'
import iconCapabilities from '@/assets/svg/nav/icon-capabilities.svg'
import iconCapitalAdequacy from '@/assets/svg/nav/icon-capital-adequacy.svg'
import iconClients from '@/assets/svg/nav/icon-clients.svg'
import iconCustomerBase from '@/assets/svg/nav/icon-customer-base.svg'
import iconDashboard from '@/assets/svg/nav/icon-dashboard.svg'
import iconDeposits from '@/assets/svg/nav/icon-deposits.svg'
import iconInvestments from '@/assets/svg/nav/icon-investments.svg'
import iconLoans from '@/assets/svg/nav/icon-loans.svg'
import iconMembership from '@/assets/svg/nav/icon-membership.svg'
import iconMyPage from '@/assets/svg/nav/icon-my-page.svg'
import iconOverview from '@/assets/svg/nav/icon-overview.svg'
import iconPerformance from '@/assets/svg/nav/icon-performance.svg'
import iconPipeline from '@/assets/svg/nav/icon-pipeline.svg'
import iconProductivity from '@/assets/svg/nav/icon-productivity.svg'
import iconProfitability from '@/assets/svg/nav/icon-profitability.svg'
import iconUsers from '@/assets/svg/nav/icon-users.svg'
import { NAV_ITEMS } from '@/shared/constants/nav-items.constants'

import type { NavIconKey } from '@/shared/constants/nav-items.constants'
import type { CSSProperties } from 'react'

interface NavMenuProps {
  privileges: string[]
  onNavigate?: () => void
  collapsed?: boolean
}

const NAV_ICON_SRC: Record<NavIconKey, string> = {
  dashboard: iconDashboard,
  users: iconUsers,
  myPage: iconMyPage,
  pipeline: iconPipeline,
  team: iconUsers,
  performance: iconPerformance,
  clients: iconClients,
  capabilities: iconCapabilities,
  auditLog: iconAuditLog,
  overview: iconOverview,
  bankDetails: iconBankDetails,
  membership: iconMembership,
  customerBase: iconCustomerBase,
  deposits: iconDeposits,
  loans: iconLoans,
  investments: iconInvestments,
  assetQuality: iconAssetQuality,
  profitability: iconProfitability,
  capitalAdequacy: iconCapitalAdequacy,
  branchAnalytics: iconBranchAnalytics,
  productivity: iconProductivity,
  businessPerformance: iconBusinessPerformance,
}

interface NavIconProps {
  icon: NavIconKey
  width?: number
  height?: number
  color?: string
}

/**
 * @description Renders one nav item's icon as a colored CSS mask of its SVG asset file, so its
 * color follows the surrounding text color (via `currentColor`) without needing a second
 * active-state icon file.
 * @param {NavIconProps} props - Which icon to render, and its size/color overrides.
 * @param {NavIconKey} props.icon - Which icon's SVG asset to render.
 * @param {number} [props.width] - Icon width in pixels. Defaults to 18.
 * @param {number} [props.height] - Icon height in pixels. Defaults to 18.
 * @param {string} [props.color] - The mask's paint color. Defaults to "currentColor" (inherits
 * from the surrounding text color).
 */
function NavIcon({ icon, width = 18, height = 18, color = 'currentColor' }: NavIconProps) {
  const maskImage = `url("${NAV_ICON_SRC[icon]}")`
  const style: CSSProperties = {
    width,
    height,
    backgroundColor: color,
    maskImage,
    WebkitMaskImage: maskImage,
    maskRepeat: 'no-repeat',
    WebkitMaskRepeat: 'no-repeat',
    maskSize: 'contain',
    WebkitMaskSize: 'contain',
    maskPosition: 'center',
    WebkitMaskPosition: 'center',
  }

  return <span aria-hidden="true" className="inline-block shrink-0" style={style} />
}

/**
 * @component
 * @param {NavMenuProps} props - The signed-in user's resolved privileges.
 * @param {string[]} props.privileges - Privilege keys from GET /identity/me; an item only renders
 * when its `privilege` is included.
 * @param {() => void} [props.onNavigate] - Called when any nav link is clicked, in addition to the
 * navigation itself.
 * @param {boolean} [props.collapsed] - Renders icon-only rows (label kept for screen readers via
 * `sr-only`, plus a native `title` tooltip for sighted mouse users) once the sidebar is collapsed.
 * Only ever set true from the desktop collapse toggle, but the label's hidden state is still
 * scoped to the `lg` breakpoint (`lg:sr-only`, not a bare `sr-only`) so the mobile off-canvas
 * drawer keeps showing full labels even if this flag is left on while the viewport narrows below
 * `lg`. Off by default.
 */
function NavMenu({ privileges, onNavigate, collapsed = false }: NavMenuProps) {
  const { t } = useTranslation()
  const items = NAV_ITEMS.filter((item) => privileges.includes(item.privilege))

  return (
    <nav className="flex flex-col gap-1">
      {items.map((item) => (
        <div key={item.path}>
          <NavLink
            to={item.path}
            onClick={onNavigate}
            title={collapsed ? t(item.labelKey) : undefined}
            className={({ isActive }) =>
              `flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13.5px] font-medium transition-colors ${
                collapsed ? 'lg:justify-center lg:gap-0' : ''
              } ${
                isActive
                  ? 'bg-primary text-white shadow-card'
                  : 'text-accent-text hover:bg-accent-hover hover:text-white'
              }`
            }
          >
            <NavIcon icon={item.icon} />
            <span className={`truncate ${collapsed ? 'lg:sr-only' : ''}`}>{t(item.labelKey)}</span>
          </NavLink>
          {item.dividerAfter && <hr className="mx-2 my-2 border-white/10" />}
        </div>
      ))}
    </nav>
  )
}

export default NavMenu
